import { applicationCountSourceTransformer } from './applicationCountSourceTransformer';
import { applicationCountSourceTypeTransformer } from './applicationCountSourceTypeTransformer';
import { jobAverageTimeTransformer } from './jobAverageTimeTransformer';
import { jobPublishedToFirstApplicationTransformer } from './jobPublishedToFirstApplicationTransformer';
import { jobFirstApplicationToHiredTransformer } from './jobFirstApplicationToHiredTransformer';
import { jobCountLocationTransformer } from './jobCountLocationTransformer';
import { jobCountInternalExternalTransformer } from './jobCountInternalExternalTransformer';
import { jobCountStateTransformer } from './jobCountStateTransformer';
import { applicationCountStatusTransformer } from './applicationCountStatusTransformer';
import { jobRequisitionApprovedToHiredTransformer } from './jobRequisitionApprovedToHiredTransformer';
import { jobRequisitionRequestedToHiredTransformer } from './jobRequisitionRequestedToHiredTransformer';
import { DateRange, StatusFilter } from '../../hooks/useChartData';
import { getQuarter, getLastNQuarters, formatQuarterLabel } from './helpers/dateHelpers';
import { aggregateQuarterData } from './helpers/dataHelpers';

interface SelectedEntity {
  id: number;
  name: string;
}

// Helper function to filter data based on date range
const filterDataByDateRange = (data: any, dateRange: DateRange) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11

  let startDate: Date;
  let endDate: Date = new Date(currentYear, currentMonth - 1, 1); // End of current month

  if (dateRange === 'last_year_quarters') {
    const last4 = getLastNQuarters(4);
    const filteredData: any = {};

    Object.entries(data).forEach(([key, value]) => {
      const parts = key.split('_');
      if (parts.length < 3) return;

      const year = parseInt(parts[1]);
      const month = parseInt(parts[2]);
      const quarter = getQuarter(month);

      // Check if this month belongs to one of our target quarters
      const isTargetQuarter = last4.some((q) => q.year === year && q.quarter === quarter);
      if (!isTargetQuarter) return;

      // Skip invalid data structures
      if (typeof value !== 'object' || value === null || !('data' in value)) return;

      const quarterKey = formatQuarterLabel(quarter, year);

      if (!filteredData[quarterKey]) {
        filteredData[quarterKey] = JSON.parse(JSON.stringify(value));
      } else {
        filteredData[quarterKey] = aggregateQuarterData(filteredData[quarterKey], value);
      }
    });

    // Sort quarters chronologically (oldest first)
    const sortedData: any = {};
    const sortedKeys = Object.keys(filteredData).sort((a, b) => {
      const [quarterA, yearA] = a.split(' ');
      const [quarterB, yearB] = b.split(' ');
      const yearDiff = parseInt(yearA) - parseInt(yearB);
      if (yearDiff !== 0) return yearDiff;
      return parseInt(quarterA.slice(1)) - parseInt(quarterB.slice(1));
    });

    sortedKeys.forEach((key) => {
      sortedData[key] = filteredData[key];
    });

    return sortedData;
  }

  switch (dateRange) {
    case 'last_12_months':
      startDate = new Date(currentYear - 1, currentMonth - 1, 1);
      break;
    case 'last_3_months':
      startDate = new Date(currentYear, currentMonth - 4, 1);
      break;
    case 'last_month':
      startDate = new Date(currentYear, currentMonth - 2, 1);
      endDate = new Date(currentYear, currentMonth - 1, 0); // End of previous month
      break;
    default:
      startDate = new Date(currentYear - 1, currentMonth - 1, 1);
  }

  const filteredData: any = {};

  Object.entries(data).forEach(([key, value]) => {
    const parts = key.split('_');
    if (parts.length >= 3) {
      const year = parseInt(parts[1]);
      const month = parseInt(parts[2]);
      const keyDate = new Date(year, month - 1, 1); // Month is 0-indexed in Date constructor

      if (keyDate >= startDate && keyDate <= endDate) {
        filteredData[key] = value;
      }
    }
  });

  return filteredData;
};

export const transformer = (
  dataSource: string,
  shownAs: string,
  groupBy: string,
  data: any,
  selectedEntities: SelectedEntity[],
  dateRange: DateRange = 'last_12_months',
  selectedStatuses: StatusFilter[] = []
) => {
  // Filter data based on date range
  const filteredData = filterDataByDateRange(data, dateRange);

  if (dataSource === 'applications') {
    if (shownAs === 'count') {
      if (groupBy === 'status') {
        return {
          ...applicationCountStatusTransformer(filteredData, selectedEntities, selectedStatuses),
          chartTitle: 'Monthly Applications by Status'
        };
      } else if (groupBy === 'source') {
        return {
          ...applicationCountSourceTransformer(filteredData, selectedEntities),
          chartTitle: 'Monthly Applications by Source'
        };
      } else {
        return {
          ...applicationCountSourceTypeTransformer(filteredData, selectedEntities),
          chartTitle: 'Monthly Applications by Source Type'
        };
      }
    } else if (shownAs === 'average') {
      if (groupBy === 'job_reference') {
        return {
          ...jobAverageTimeTransformer(filteredData, selectedEntities),
          chartTitle: 'Average Applications per Job'
        };
      }
    }
  } else if (dataSource === 'jobs') {
    if (shownAs === 'count') {
      if (groupBy === 'location') {
        return {
          ...jobCountLocationTransformer(filteredData, selectedEntities),
          chartTitle: 'Monthly Jobs by Location'
        };
      } else if (groupBy === 'internal_external') {
        return {
          ...jobCountInternalExternalTransformer(filteredData, selectedEntities),
          chartTitle: 'Monthly Jobs by Internal/External'
        };
      } else if (groupBy === 'job_state') {
        return {
          ...jobCountStateTransformer(filteredData, selectedEntities, selectedStatuses),
          chartTitle: 'Monthly Jobs by State'
        };
      }
    } else if (shownAs === 'average_time') {
      if (groupBy === 'published_to_hired') {
        return {
          ...jobPublishedToFirstApplicationTransformer(filteredData, selectedEntities),
          chartTitle: 'Monthly Average Time to Hire from Published Date'
        };
      } else if (groupBy === 'first_application_to_hired') {
        return {
          ...jobFirstApplicationToHiredTransformer(filteredData, selectedEntities),
          chartTitle: 'Monthly Average Time to Hire from First Application Date'
        };
      } else if (groupBy === 'requisition_approved_to_hired') {
        return {
          ...jobRequisitionApprovedToHiredTransformer(filteredData, selectedEntities),
          chartTitle: 'Average Days from Requisition Approved to Hired'
        };
      } else if (groupBy === 'requisition_requested_to_hired') {
        return {
          ...jobRequisitionRequestedToHiredTransformer(filteredData, selectedEntities),
          chartTitle: 'Average Days from Requisition Requested to Hired'
        };
      }
    }
  }

  return {
    labels: [],
    datasets: [],
    chartTitle: 'No data available'
  };
};
